import {usePrintTarget} from "../hooks/usePrintTarget";
import React from "react";
import {
  Printer,
  FileDown,
  Building,
  CreditCard,
  Wallet,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  Calendar,
  DollarSign,
  AlertCircle
} from "lucide-react";
import AccountingService from "../lib/edesisAccountingService";
import { csvRow } from "../lib/csv";

const MONTH_NAMES = {
  "01": "Ocak",
  "02": "Şubat",
  "03": "Mart",
  "04": "Nisan",
  "05": "Mayıs",
  "06": "Haziran",
  "07": "Temmuz",
  "08": "Ağustos",
  "09": "Eylül",
  "10": "Ekim",
  "11": "Kasım",
  "12": "Aralık"
};

function formatMoney(amount) {
  if (amount === undefined || amount === null || isNaN(amount)) return "₺0,00";
  return "₺" + Number(amount).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

export function MonthlyReportPreview({ month = "2026-10", data = {}, onPrint }) {
  const printRef=usePrintTarget();
  // Parse year and month
  const [yearStr, monthStr] = month.split("-");
  const monthName = MONTH_NAMES[monthStr] || monthStr;
  const monthLabel = `${monthName} ${yearStr}`;

  // Month date range
  const daysInMonth = new Date(parseInt(yearStr, 10), parseInt(monthStr, 10), 0).getDate();
  const periodRange = `01.${monthStr}.${yearStr} - ${daysInMonth}.${monthStr}.${yearStr}`;
  const reportDate = new Date().toLocaleDateString("tr-TR");
  const reportNo = `RPR-${yearStr}${monthStr}-01`;

  // Use one authoritative list, including an empty successful result.
  const monthlyPayments = (() => {
    const list = [];

    // From AccountingService collections
    const payments = Array.isArray(data.payments) ? data.payments : AccountingService.getCollections();
    payments.forEach(t => {
      if (t.is_cancelled || t.status === "İptal Edildi" || t.status === "cancelled") return;
      const dt = (t.tahsilatTarihi || t.payment_date || t.date || "").slice(0, 7);
      if (dt === month) {
        list.push({
          id: t.id,
          code: t.odemeKodu || t.receipt_no || `THS-${t.id}`,
          date: (t.tahsilatTarihi || t.payment_date || t.date || "").slice(0, 10),
          studentName: t.musteriUnvan || t.student_name || "Öğrenci",
          account: t.kasaBankaAdi || t.bank || t.account || t.method || "Nakit Ana Kasa",
          amount: Number(t.tahsilatTutari ?? t.amount) || 0,
          note: t.aciklama || t.note || "Taksit Tahsilatı"
        });
      }
    });

    // Sort by date descending
    return list.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  })();

  // 2. Filter installments due in this month from AccountingService taksitler
  const monthlyDueInstallments = (() => {
    return (Array.isArray(data.installments) ? data.installments : AccountingService.getInstallments())
      .filter(t => Number(t.contract_status) !== 3 && (t.vadeTarihi || t.due_date || "").slice(0, 7) === month)
      .map(t => ({
        id: t.id,
        studentName: t.ogrenciAdiSoyadi || t.ogrenciAdi || t.musteriUnvan || t.student_name || "Öğrenci",
        className: t.sinifAdi || "—",
        contractNo: t.gelirAciklama ? t.gelirAciklama.replace("Sözleşme: ", "") : (t.sozlesmeNo || t.sozlesme_no || t.gelirKodu),
        installmentNo: t.taksitNo ?? t.installment_no,
        amount: Number(t.taksitTutari ?? t.amount) || 0,
        paid: Number(t.tahsilEdilen ?? t.paid_amount) || 0,
        remaining: Number(t.kalanTutar ?? Math.max(0, Number(t.amount || 0) - Number(t.paid_amount || 0))),
        dueDate: (t.vadeTarihi || t.due_date || "").slice(0, 10),
        status: Number(t.kalanTutar ?? Math.max(0, Number(t.amount || 0) - Number(t.paid_amount || 0))) === 0 ? "Ödendi" : (t.durumText || "Bekliyor")
      }))
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  })();

  // 3. Filter expenses in this month
  const monthlyExpenses = (() => {
    const list = [];
    (data.expenses || []).forEach(e => {
      const dt = (e.expense_date || e.date || "").slice(0, 7);
      if (dt === month) {
        list.push({
          id: e.id,
          title: e.title || e.category || "Genel Gider",
          category: e.category || "İşletme",
          date: (e.expense_date || e.date || "").slice(0, 10),
          amount: Number(e.amount) || 0,
          method: e.payment_method || "Banka"
        });
      }
    });

    return list;
  })();

  // Calculations
  const totalCollected = monthlyPayments.reduce((acc, p) => acc + Math.round(p.amount * 100), 0) / 100;
  const totalDueInstallments = monthlyDueInstallments.reduce((acc, t) => acc + Math.round(t.amount * 100), 0) / 100;
  const totalExpenses = monthlyExpenses.reduce((acc, e) => acc + Math.round(e.amount * 100), 0) / 100;
  const netCashFlow = Math.round((totalCollected - totalExpenses) * 100) / 100;

  // Account breakdown for this month
  const accountBreakdown = (() => {
    const map = {
      "T.C. VAKIFLAR BANKASI (Nevin Çakmak)": 0,
      "T.C. HALKBANK İNEGÖL ŞUBESİ": 0,
      "NAKİT ANA KASA": 0,
      "POS / KREDİ KARTI": 0,
      "SENET / DİĞER": 0
    };

    monthlyPayments.forEach(p => {
      const acc = (p.account || "").toUpperCase();
      if (acc.includes("VAKIF") || acc.includes("NEVİN ÇAKMAK") || acc.includes("BİREYSEL")) {
        map["T.C. VAKIFLAR BANKASI (Nevin Çakmak)"] += p.amount;
      } else if (acc.includes("HALK")) {
        map["T.C. HALKBANK İNEGÖL ŞUBESİ"] += p.amount;
      } else if (acc.includes("POS") || acc.includes("KART")) {
        map["POS / KREDİ KARTI"] += p.amount;
      } else if (acc.includes("SENET")) {
        map["SENET / DİĞER"] += p.amount;
      } else {
        map["NAKİT ANA KASA"] += p.amount;
      }
    });

    return map;
  })();

  // Excel / CSV Export
  const handleExportCSV = () => {
    let csv = "\uFEFF"; // UTF-8 BOM for Turkish character support in Excel
    csv += "T.C. MİLLÎ EĞİTİM BAKANLIĞI - ÖZEL İNEGÖL ÇÖZÜM KURS MERKEZİ\n";
    csv += `AYLIK MUHASEBE VE KASA RAPORU - ${monthLabel}\n`;
    csv += `Rapor No: ${reportNo} - Tarih: ${reportDate}\n\n`;

    csv += "--- FİNANSAL ÖZET ---\n";
    csv += `Toplam Gerçekleşen Tahsilat;${totalCollected}\n`;
    csv += `Bu Ay Vadesi Gelen Taksitler;${totalDueInstallments}\n`;
    csv += `Toplam Kurum Giderleri;${totalExpenses}\n`;
    csv += `Net Kasa Akışı;${netCashFlow}\n\n`;

    csv += "--- AY İÇİ TAHSİLATLAR DÖKÜMÜ ---\n";
    csv += "Tarih;Makbuz No;Öğrenci Adı Soyadı;Kasa/Hesap;Tutar;Açıklama\n";
    monthlyPayments.forEach(p => {
      csv += csvRow([p.date,p.code,p.studentName,p.account,p.amount,p.note]) + "\n";
    });

    csv += "\n--- VADESİ GELEN TAKSİTLER ---\n";
    csv += "Vade Tarihi;Öğrenci Adı Soyadı;Sınıf;Sözleşme No;Taksit No;Tutar;Durum\n";
    monthlyDueInstallments.forEach(t => {
      csv += csvRow([t.dueDate,t.studentName,t.className,t.contractNo,t.installmentNo,t.amount,t.status]) + "\n";
    });

    csv += "\n--- KURUM GİDERLERİ DÖKÜMÜ ---\n";
    csv += "Tarih;Gider Başlığı;Kategori;Tutar;Ödeme Yöntemi\n";
    monthlyExpenses.forEach(e => {
      csv += csvRow([e.date,e.title,e.category,e.amount,e.method]) + "\n";
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    if (typeof window !== "undefined" && window.URL && typeof window.URL.createObjectURL === "function") {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `inegol-cozum-muhasebe-raporu-${month}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  return (
    <div ref={printRef} className="monthly-report-preview-container" style={{ marginTop: "24px" }}>
      {/* Önizleme Kontrol Başlığı */}
      <div 
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "16px",
          background: "#fff",
          padding: "14px 20px",
          borderRadius: "10px",
          border: "1px solid #e2e8f0"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div 
            style={{ 
              width: "36px", 
              height: "36px", 
              borderRadius: "8px", 
              background: "#eff6ff", 
              color: "#2563eb", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center" 
            }}
          >
            <Calendar size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
              Canlı Rapor Önizlemesi · {monthLabel}
            </h3>
            <span style={{ fontSize: "12px", color: "#64748b" }}>
              Kurumsal A4 resmi antetli döküm ve tahsilat/gider tablosu
            </span>
          </div>
        </div>

        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={handleExportCSV}
            className="btn small secondary"
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
            title="Tüm ay verilerini Excel uyumlu CSV olarak indir"
          >
            <FileDown size={15} /> Excel (.CSV) İndir
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="btn small"
            style={{ 
              display: "inline-flex", 
              alignItems: "center", 
              gap: "6px", 
              background: "#2563eb", 
              color: "#fff" 
            }}
            title="A4 formatında yazdır veya PDF olarak kaydet"
          >
            <Printer size={15} /> Yazdır / PDF Kaydet
          </button>
        </div>
      </div>

      {/* A4 KURUMSAL RAPOR BELGESİ (CANLI ÖNİZLEME) */}
      <div 
        className="report-document-sheet"
        style={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #cbd5e1",
          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
          padding: "36px 40px",
          maxWidth: "960px",
          margin: "0 auto",
          color: "#0f172a",
          fontSize: "13px",
          lineHeight: "1.5"
        }}
      >
        {/* Antet ve Kurum Başlığı */}
        <div 
          style={{
            borderBottom: "2px solid #0f172a",
            paddingBottom: "16px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "16px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div 
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #1e3a8a, #2563eb)",
                color: "#fff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "800",
                fontSize: "16px",
                letterSpacing: "0.5px"
              }}
            >
              <span>ÇÖZÜM</span>
              <span style={{ fontSize: "9px", opacity: 0.9 }}>KURS</span>
            </div>
            <div>
              <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", letterSpacing: "1px" }}>
                T.C. MİLLÎ EĞİTİM BAKANLIĞI
              </div>
              <h1 style={{ margin: "2px 0 0 0", fontSize: "18px", fontWeight: "800", color: "#0f172a" }}>
                ÖZEL İNEGÖL ÇÖZÜM ÖZEL ÖĞRETİM KURSU
              </h1>
              <div style={{ fontSize: "11px", color: "#475569", marginTop: "2px" }}>
                Kurum Kodu: <b>99916521</b> · Osmaniye Mah. Ahmet Akyollu Cad. No:19 İnegöl / BURSA · Tel: 0 224 715 00 25
              </div>
            </div>
          </div>

          <div style={{ textAlign: "right", fontSize: "11px", color: "#475569" }}>
            <div 
              style={{
                display: "inline-block",
                background: "#f1f5f9",
                padding: "4px 10px",
                borderRadius: "6px",
                fontWeight: "700",
                color: "#0f172a",
                marginBottom: "4px",
                fontSize: "12px"
              }}
            >
              {reportNo}
            </div>
            <div><b>Rapor Dönemi:</b> {periodRange}</div>
            <div><b>Düzenleme Tarihi:</b> {reportDate}</div>
            <div><b>Belge Türü:</b> Aylık Mali & Kasa Raporu</div>
          </div>
        </div>

        {/* Belge Başlığı */}
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <h2 style={{ margin: 0, fontSize: "17px", fontWeight: "800", letterSpacing: "0.5px", color: "#1e3a8a", textTransform: "uppercase" }}>
            {monthLabel} DÖNEMİ AYLIK GELİR, GİDER VE KASA HAREKET RAPORU
          </h2>
          <span style={{ fontSize: "12px", color: "#64748b" }}>
            Özel İnegöl Çözüm Özel Öğretim Kursu Resmi Muhasebe İcmali
          </span>
        </div>

        {/* 4 Ana Finansal Özet Kartı */}
        <div 
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "12px",
            marginBottom: "24px"
          }}
        >
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "12px 14px", borderRadius: "8px" }}>
            <span style={{ fontSize: "11px", fontWeight: "700", color: "#166534", textTransform: "uppercase", display: "block" }}>
              ✓ Gerçekleşen Tahsilat
            </span>
            <div style={{ fontSize: "18px", fontWeight: "800", color: "#15803d", marginTop: "2px" }}>
              {formatMoney(totalCollected)}
            </div>
            <span style={{ fontSize: "11px", color: "#166534" }}>{monthlyPayments.length} Fiş / Makbuz</span>
          </div>

          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", padding: "12px 14px", borderRadius: "8px" }}>
            <span style={{ fontSize: "11px", fontWeight: "700", color: "#1e40af", textTransform: "uppercase", display: "block" }}>
              📅 Bu Ay Vadesi Dolan
            </span>
            <div style={{ fontSize: "18px", fontWeight: "800", color: "#1d4ed8", marginTop: "2px" }}>
              {formatMoney(totalDueInstallments)}
            </div>
            <span style={{ fontSize: "11px", color: "#1e40af" }}>{monthlyDueInstallments.length} Taksit Tahakkuku</span>
          </div>

          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", padding: "12px 14px", borderRadius: "8px" }}>
            <span style={{ fontSize: "11px", fontWeight: "700", color: "#991b1b", textTransform: "uppercase", display: "block" }}>
              📉 Dönem Kurum Gideri
            </span>
            <div style={{ fontSize: "18px", fontWeight: "800", color: "#b91c1c", marginTop: "2px" }}>
              {formatMoney(totalExpenses)}
            </div>
            <span style={{ fontSize: "11px", color: "#991b1b" }}>{monthlyExpenses.length} Gider Kalemi</span>
          </div>

          <div style={{ background: netCashFlow >= 0 ? "#f8fafc" : "#fff1f2", border: "1px solid #cbd5e1", padding: "12px 14px", borderRadius: "8px" }}>
            <span style={{ fontSize: "11px", fontWeight: "700", color: "#334155", textTransform: "uppercase", display: "block" }}>
              ⚖️ Net Kasa / Fark
            </span>
            <div style={{ fontSize: "18px", fontWeight: "800", color: netCashFlow >= 0 ? "#0f172a" : "#dc2626", marginTop: "2px" }}>
              {formatMoney(netCashFlow)}
            </div>
            <span style={{ fontSize: "11px", color: "#64748b" }}>
              {netCashFlow >= 0 ? "Pozitif Nakit Akışı" : "Açık / Destek İhtiyacı"}
            </span>
          </div>
        </div>

        {/* 1. Kasa ve Banka Tahsilat Yöntem Dağılımı */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={{ fontSize: "13px", fontWeight: "700", textTransform: "uppercase", color: "#334155", borderBottom: "1px solid #e2e8f0", paddingBottom: "6px", marginBottom: "10px" }}>
            1. Tahsilat Kanalları & Kasa / Banka Dağılımı
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "10px" }}>
            {Object.entries(accountBreakdown).map(([name, val], idx) => (
              <div key={idx} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "10px", borderRadius: "6px" }}>
                <span style={{ fontSize: "11px", color: "#64748b", display: "block", fontWeight: "600" }}>{name}</span>
                <b style={{ fontSize: "14px", color: "#0f172a" }}>{formatMoney(val)}</b>
              </div>
            ))}
          </div>
        </div>

        {/* 2. Ay İçi Gerçekleşen Tahsilatlar Tablosu */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", borderBottom: "1px solid #e2e8f0", paddingBottom: "6px" }}>
            <h3 style={{ margin: 0, fontSize: "13px", fontWeight: "700", textTransform: "uppercase", color: "#334155" }}>
              2. Seçilen Ay İçi Gerçekleşen Tahsilatlar ({monthlyPayments.length} Kayıt)
            </h3>
            <span style={{ fontSize: "11px", color: "#64748b" }}>Toplam: <b>{formatMoney(totalCollected)}</b></span>
          </div>

          {monthlyPayments.length > 0 ? (
            <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11.5px", textAlign: "left" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#475569" }}>
                    <th style={{ padding: "8px 10px" }}>Tarih</th>
                    <th style={{ padding: "8px 10px" }}>Makbuz / Dekont No</th>
                    <th style={{ padding: "8px 10px" }}>Öğrenci / Müşteri</th>
                    <th style={{ padding: "8px 10px" }}>Tahsilat Hesabı</th>
                    <th style={{ padding: "8px 10px", textAlign: "right" }}>Tutar</th>
                    <th style={{ padding: "8px 10px" }}>Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyPayments.map((p, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 10px", fontWeight: "600" }}>{p.date}</td>
                      <td style={{ padding: "8px 10px", color: "#2563eb", fontWeight: "600" }}>{p.code}</td>
                      <td style={{ padding: "8px 10px", fontWeight: "700" }}>{p.studentName}</td>
                      <td style={{ padding: "8px 10px", color: "#475569" }}>{p.account}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: "700", color: "#16a34a" }}>
                        {formatMoney(p.amount)}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#64748b" }}>{p.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: "18px", textAlign: "center", background: "#f8fafc", borderRadius: "6px", border: "1px dashed #cbd5e1", color: "#64748b", fontSize: "12px" }}>
              Seçilen {monthLabel} ayı için sisteme girilmiş tahsilat kaydı bulunmamaktadır.
            </div>
          )}
        </div>

        {/* 3. Ay İçi Vadesi Gelen Taksitler Tablosu */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", borderBottom: "1px solid #e2e8f0", paddingBottom: "6px" }}>
            <h3 style={{ margin: 0, fontSize: "13px", fontWeight: "700", textTransform: "uppercase", color: "#334155" }}>
              3. Bu Ay Vadesi Dolan Taksitler & Tahakkuk ({monthlyDueInstallments.length} Taksit)
            </h3>
            <span style={{ fontSize: "11px", color: "#64748b" }}>Toplam Tahakkuk: <b>{formatMoney(totalDueInstallments)}</b></span>
          </div>

          {monthlyDueInstallments.length > 0 ? (
            <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "6px", maxHeight: "280px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11.5px", textAlign: "left" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#475569" }}>
                    <th style={{ padding: "8px 10px" }}>Vade Tarihi</th>
                    <th style={{ padding: "8px 10px" }}>Öğrenci Adı Soyadı</th>
                    <th style={{ padding: "8px 10px" }}>Sınıf</th>
                    <th style={{ padding: "8px 10px" }}>Sözleşme No</th>
                    <th style={{ padding: "8px 10px" }}>Taksit No</th>
                    <th style={{ padding: "8px 10px", textAlign: "right" }}>Taksit Tutarı</th>
                    <th style={{ padding: "8px 10px", textAlign: "center" }}>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyDueInstallments.map((t, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 10px", fontWeight: "600" }}>{t.dueDate}</td>
                      <td style={{ padding: "8px 10px", fontWeight: "700" }}>{t.studentName}</td>
                      <td style={{ padding: "8px 10px", color: "#475569" }}>{t.className}</td>
                      <td style={{ padding: "8px 10px", color: "#64748b" }}>{t.contractNo}</td>
                      <td style={{ padding: "8px 10px" }}>#{t.installmentNo}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: "700", color: "#1e40af" }}>
                        {formatMoney(t.amount)}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center" }}>
                        <span 
                          style={{
                            fontSize: "10.5px",
                            fontWeight: "600",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            background: t.status === "Ödendi" ? "#dcfce7" : (t.status === "Gecikmede" ? "#fee2e2" : "#f1f5f9"),
                            color: t.status === "Ödendi" ? "#15803d" : (t.status === "Gecikmede" ? "#b91c1c" : "#475569")
                          }}
                        >
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: "18px", textAlign: "center", background: "#f8fafc", borderRadius: "6px", border: "1px dashed #cbd5e1", color: "#64748b", fontSize: "12px" }}>
              Seçilen {monthLabel} ayı için vadesi gelen taksit kaydı bulunmamaktadır.
            </div>
          )}
        </div>

        {/* 4. Kurum Giderleri Tablosu */}
        <div style={{ marginBottom: "32px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", borderBottom: "1px solid #e2e8f0", paddingBottom: "6px" }}>
            <h3 style={{ margin: 0, fontSize: "13px", fontWeight: "700", textTransform: "uppercase", color: "#334155" }}>
              4. Dönem Kurum Giderleri Dökümü ({monthlyExpenses.length} Kalem)
            </h3>
            <span style={{ fontSize: "11px", color: "#64748b" }}>Toplam Gider: <b>{formatMoney(totalExpenses)}</b></span>
          </div>

          <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11.5px", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#475569" }}>
                  <th style={{ padding: "8px 10px" }}>Tarih</th>
                  <th style={{ padding: "8px 10px" }}>Gider Kalemi</th>
                  <th style={{ padding: "8px 10px" }}>Kategori</th>
                  <th style={{ padding: "8px 10px" }}>Ödeme Kanalı</th>
                  <th style={{ padding: "8px 10px", textAlign: "right" }}>Tutar</th>
                </tr>
              </thead>
              <tbody>
                {monthlyExpenses.map((e, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "8px 10px", fontWeight: "600" }}>{e.date}</td>
                    <td style={{ padding: "8px 10px", fontWeight: "700" }}>{e.title}</td>
                    <td style={{ padding: "8px 10px", color: "#64748b" }}>{e.category}</td>
                    <td style={{ padding: "8px 10px", color: "#475569" }}>{e.method}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: "700", color: "#dc2626" }}>
                      {formatMoney(e.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 5. Resmi Kaşe & Yetkili İmzalar */}
        <div 
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "40px",
            borderTop: "2px solid #0f172a",
            paddingTop: "20px",
            textAlign: "center",
            marginTop: "30px"
          }}
        >
          <div>
            <div style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a" }}>
              MUHASEBE VE FİNANS YETKİLİSİ
            </div>
            <div style={{ fontSize: "11px", color: "#64748b" }}>Osman SEYMEN</div>
            <div style={{ height: "55px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>[İmza / Kaşe]</span>
            </div>
            <div style={{ fontSize: "11px", color: "#64748b" }}>Tarih: {reportDate}</div>
          </div>

          <div>
            <div style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a" }}>
              KURUM MÜDÜRÜ
            </div>
            <div style={{ fontSize: "11px", color: "#64748b" }}>Nevin ÇAKMAK</div>
            <div style={{ height: "55px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>[Resmi Mühür / Islak İmza]</span>
            </div>
            <div style={{ fontSize: "11px", color: "#64748b" }}>Özel İnegöl Çözüm Özel Öğretim Kursu</div>
          </div>
        </div>

        {/* Dipnot Bilgilendirme */}
        <div 
          style={{
            marginTop: "24px",
            paddingTop: "12px",
            borderTop: "1px dashed #cbd5e1",
            display: "flex",
            justifyContent: "space-between",
            fontSize: "10px",
            color: "#94a3b8"
          }}
        >
          <span>Bu rapor Özel İnegöl Çözüm Kursu Bilgi İşlem Sistemi üzerinden otomatik üretilmiştir.</span>
          <span>Sayfa 1 / 1 · Güvenlik Kodu: MEB-EDS-99916521</span>
        </div>
      </div>
    </div>
  );
}
